import pLimit from "p-limit";

import { encodeKey } from "../FileGrid";

const WEBDAV_ENDPOINT = "/webdav/";

export interface MoveOutcome {
  key: string;
  ok: boolean;
  error?: string;
}

/**
 * Create a folder if it is not already there. MKCOL answers 405 when the
 * collection exists, which is a success for our purposes.
 */
export async function ensureFolder(path: string) {
  const clean = path.replace(/^\/+|\/+$/g, "");
  if (!clean) return;

  // Create each level in turn so nested destinations work.
  const parts = clean.split("/");
  for (let i = 0; i < parts.length; i++) {
    const sub = parts.slice(0, i + 1).join("/");
    const res = await fetch(`${WEBDAV_ENDPOINT}${encodeKey(sub)}`, {
      method: "MKCOL",
      headers: { "X-FlareDrive-Client": "web" },
    });
    if (!res.ok && res.status !== 405)
      throw new Error(`Could not create folder "${sub}" (${res.status})`);
  }
}

/**
 * Move objects into a destination folder, a few at a time. R2 has no rename,
 * so the server copies then deletes each object; large files take a while.
 */
export async function moveItems(
  keys: string[],
  destFolder: string,
  onProgress?: (done: number, total: number, current: string) => void
): Promise<MoveOutcome[]> {
  const dest = destFolder.replace(/^\/+|\/+$/g, "");
  const limit = pLimit(3);
  let done = 0;

  const tasks = keys.map((key) =>
    limit(async (): Promise<MoveOutcome> => {
      const name = key.replace(/\/$/, "").split("/").pop()!;
      const target = dest ? `${dest}/${name}` : name;

      if (target === key.replace(/\/$/, "")) {
        done++;
        onProgress?.(done, keys.length, name);
        return { key, ok: true };
      }

      try {
        const destinationUrl = new URL(
          `${WEBDAV_ENDPOINT}${encodeKey(target)}`,
          window.location.href
        );
        const res = await fetch(
          `${WEBDAV_ENDPOINT}${encodeKey(key.replace(/\/$/, ""))}`,
          {
            method: "MOVE",
            headers: {
              Destination: destinationUrl.href,
              "X-FlareDrive-Client": "web",
            },
          }
        );
        if (!res.ok)
          return { key, ok: false, error: `${res.status} ${res.statusText}` };
        return { key, ok: true };
      } catch (e: any) {
        return { key, ok: false, error: e?.message ?? "Network error" };
      } finally {
        done++;
        onProgress?.(done, keys.length, name);
      }
    })
  );

  return Promise.all(tasks);
}
